import { DOCUMENT } from '@angular/common';
import { inject, Pipe, PipeTransform } from '@angular/core';
import { DomSanitizer, SafeHtml } from '@angular/platform-browser';
import createDOMPurify from 'dompurify';

// === CSS Allowlist Configuration ===
const ALLOWED_CSS = new Set(['text-align', 'font-size', 'color', 'background-color', 'width', 'height', 'border-style', 'border-width']);

// === Helper Functions ===

function clamp(n: number, min: number, max: number): number {
  return Math.max(min, Math.min(max, n));
}

function isHexColor(value: string): boolean {
  return /^#([0-9a-f]{3}|[0-9a-f]{6})$/i.test(value);
}

function sanitizeCssDeclaration(prop: string, valueRaw: string): string | null {
  const value = valueRaw.trim().toLowerCase();

  // Block anything URL-ish or expression-based (CSS injection vectors)
  if (value.includes('url(') || value.includes('expression(')) {
    return null;
  }

  switch (prop) {
    case 'text-align':
      if (/^(left|right|center|justify)$/.test(value)) {
        return `text-align:${value};`;
      }
      return null;

    case 'font-size': {
      const match = value.match(/^(\d+(\.\d+)?)(pt|px|em|rem|%)$/);
      if (!match) return null;

      const num = Number(match[1]);
      const unit = match[3];

      // Clamp values to prevent abuse (e.g., 99999pt)
      if (unit === 'pt') return `font-size:${clamp(num, 6, 48)}pt;`;
      if (unit === 'px') return `font-size:${clamp(num, 8, 72)}px;`;
      if (unit === '%') return `font-size:${clamp(num, 50, 300)}%;`;
      // em/rem
      return `font-size:${clamp(num, 0.5, 3)}${unit};`;
    }

    case 'color':
    case 'background-color':
      if (isHexColor(value)) {
        return `${prop}:${value};`;
      }
      return null;

    case 'width':
    case 'height': {
      const match = value.match(/^(\d+(\.\d+)?)px$/);
      if (!match) return null;
      const num = clamp(Number(match[1]), 1, 2000);
      return `${prop}:${num}px;`;
    }

    case 'border-style':
      if (/^(none|solid|dashed|dotted|double)$/.test(value)) {
        return `border-style:${value};`;
      }
      return null;

    case 'border-width': {
      const match = value.match(/^(\d+(\.\d+)?)px$/);
      if (!match) return null;
      const num = clamp(Number(match[1]), 0, 20);
      return `border-width:${num}px;`;
    }

    default:
      return null;
  }
}

function filterStyle(style: string): string {
  const output: string[] = [];

  for (const chunk of style.split(';')) {
    const colonIndex = chunk.indexOf(':');
    if (colonIndex === -1) continue;

    const prop = chunk.slice(0, colonIndex).trim().toLowerCase();
    if (!ALLOWED_CSS.has(prop)) continue;

    const val = chunk.slice(colonIndex + 1);
    const declaration = sanitizeCssDeclaration(prop, val);
    if (declaration) {
      output.push(declaration);
    }
  }

  return output.join('');
}

function isSafeId(id: string): boolean {
  return /^[A-Za-z][A-Za-z0-9_-]{0,31}$/.test(id);
}

function isSafeHref(href: string): boolean {
  const h = href.trim();
  if (h.startsWith('#')) {
    return /^#[A-Za-z][A-Za-z0-9_-]{0,31}$/.test(h);
  }
  return /^(https?:\/\/|mailto:)/i.test(h);
}

function isSafeImgSrc(src: string): boolean {
  return /^https:\/\//i.test(src.trim());
}

function configureHooks(domPurify: ReturnType<typeof createDOMPurify>): void {
  domPurify.addHook('afterSanitizeAttributes', (node: Element) => {
    if (node.nodeType !== 1) return;

    // Style: keep only allowlisted CSS properties with validated values
    if (node.hasAttribute('style')) {
      const cleaned = filterStyle(node.getAttribute('style') || '');
      if (cleaned) {
        node.setAttribute('style', cleaned);
      } else {
        node.removeAttribute('style');
      }
    }

    // IDs: only allow safe anchor-style IDs
    if (node.hasAttribute('id')) {
      const id = node.getAttribute('id') || '';
      if (!isSafeId(id)) {
        node.removeAttribute('id');
      }
    }

    // Links: validate href, target, and auto-add rel for security
    if (node.tagName === 'A') {
      if (node.hasAttribute('href')) {
        const href = node.getAttribute('href') || '';
        if (!isSafeHref(href)) {
          node.removeAttribute('href');
        }
      }

      if (node.hasAttribute('target')) {
        const target = (node.getAttribute('target') || '').toLowerCase();
        if (target !== '_self' && target !== '_blank') {
          node.removeAttribute('target');
        }
      }

      // Prevent tabnabbing: auto-add rel="noopener noreferrer" for _blank links
      const target = (node.getAttribute('target') || '').toLowerCase();
      if (target === '_blank') {
        node.setAttribute('rel', 'noopener noreferrer');
      }
    }

    // Images: validate src (HTTPS only)
    if (node.tagName === 'IMG') {
      const src = node.getAttribute('src') || '';
      if (!isSafeImgSrc(src)) {
        node.removeAttribute('src');
      }
    }
  });
}

/**
 * One DOMPurify instance shared by every pipe instance. Isolated from the
 * global DOMPurify singleton so our hooks can't be contaminated by (or leak
 * into) any other consumer, and created lazily so the window reference is
 * only touched once a pipe actually runs.
 */
let sharedDomPurify: ReturnType<typeof createDOMPurify> | undefined;

/**
 * Sanitizes HTML content using an isolated DOMPurify instance with a strict allowlist.
 * Prevents XSS attacks while allowing safe formatting, links, and images.
 *
 * Security features:
 * - Tag and attribute allowlists
 * - CSS property allowlist with value validation and clamping
 * - Safe anchor IDs only
 * - HTTPS-only image sources
 * - Auto-adds rel="noopener noreferrer" for external links
 *
 * @usageNotes
 * ### Examples
 * @example <div [innerHTML]="untrustedHtml | safeHtml"></div>
 */
@Pipe({
  name: 'safeHtml',
})
export class SafeHtmlPipe implements PipeTransform {
  private readonly sanitizer = inject(DomSanitizer);
  private readonly documentRef = inject(DOCUMENT);

  public transform(html: string | null | undefined): SafeHtml {
    if (!html) {
      return '';
    }

    if (!sharedDomPurify) {
      sharedDomPurify = createDOMPurify(this.documentRef.defaultView ?? undefined);
      configureHooks(sharedDomPurify);
    }

    const clean = sharedDomPurify.sanitize(html, {
      ALLOWED_TAGS: [
        'a',
        'p',
        'br',
        'hr',
        'div',
        'span',
        'strong',
        'b',
        'em',
        'i',
        'u',
        's',
        'sub',
        'sup',
        'h1',
        'h2',
        'h3',
        'h4',
        'h5',
        'h6',
        'pre',
        'code',
        'blockquote',
        'ol',
        'ul',
        'li',
        'img',
        'table',
        'thead',
        'tbody',
        'tr',
        'th',
        'td',
      ],
      ALLOWED_ATTR: ['id', 'href', 'target', 'rel', 'src', 'alt', 'title', 'style', 'width', 'height'],
      ALLOW_DATA_ATTR: false,
    });

    return this.sanitizer.bypassSecurityTrustHtml(clean);
  }
}
